import { takeEvery, takeLatest, take, put, call, fork, select } from 'redux-saga/effects'
import { normalize } from 'normalizr'
import { saveAs } from 'file-saver'

import { indicateProgress } from 'api'
import { tokenSelector, userSelector } from 'auth/selectors'
import { LOGOUT } from 'auth/constants'
import { clearEntity } from 'entities/actions'
import { setReservations } from 'dashboard-transactions/actions'
import { isMobileAgent } from 'shared/responsive-helpers'
import { apiLanguageCodeSelector } from 'i18n/selectors'

import * as constants from './constants'
import * as actions from './actions'
import * as selectors from './selectors'
import * as api from './api'
import { reservationSchema } from './schemas'
import { guestUserReservationsSelector } from 'manage-guest-user-booking/selectors'
import {
    setReservationsByParam,
    getReservationsByParamError,
    setGuestReservationCancelAmendPolicy,
} from 'manage-guest-user-booking/actions'
import { GET_RESERVATIONS_BY_PARAM } from 'manage-guest-user-booking/constants'

export function* locationDetailsSaga() {
    yield takeEvery(constants.FETCH_LOCATION_DETAILS, function* ({ locationId }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const locationData = yield call(
                indicateProgress,
                ['locationDetails'],
                api.getLocationData,
                locationId,
                apiLanguageCode
            )

            yield put(actions.receivedLocationDetails(locationData))
        } catch (exception) {}
    })
}

export function* locationRatesSaga() {
    yield takeEvery(constants.FETCH_RATES, function* ({ criteria, requestId, customerFlowType }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const token = yield select(tokenSelector)
            const response = yield call(
                indicateProgress,
                ['locationRates'],
                api.getRates,
                criteria,
                apiLanguageCode,
                token,
                customerFlowType
            )
            yield put(actions.receivedLocationRates(response, requestId))
        } catch (exception) {
            yield put(actions.failedToFetchRates(criteria, requestId, exception))
        }
    })
}

export function* locationRateDetailsSaga() {
    yield takeEvery(
        constants.FETCH_RATE_DETAILS,
        function* ({ locationId, rateId, beginTime, endTime, customerFlowType, startedWithProductId }) {
            try {
                const apiLanguageCode = yield select(apiLanguageCodeSelector)
                const details = yield call(
                    api.getRateDetails,
                    locationId,
                    rateId,
                    beginTime,
                    endTime,
                    apiLanguageCode,
                    customerFlowType,
                    startedWithProductId
                )
                yield put(actions.receivedRateDetails(details))
            } catch (exception) {}
        }
    )
}

export function* getReservationsSaga() {
    yield takeEvery(constants.GET_RESERVATIONS, function* ({ offset, limit, status, requestId }) {
        try {
            yield indicateProgress(['getReservations'], function* () {
                const user = yield select(userSelector)
                if (user) {
                    const useWaitingList =
                        [constants.STATUS_WAITING_LIST, constants.STATUS_CONFIRMED].indexOf(status) > -1
                    const apiLanguageCode = yield select(apiLanguageCodeSelector)
                    const response = yield call(
                        useWaitingList ? api.getWaitingListRecordsByAccount : api.getReservations,
                        user.token,
                        offset,
                        limit,
                        status === constants.STATUS_WAITING_LIST ? null : status,
                        apiLanguageCode
                    )

                    yield put(actions.receivedReservations(response, requestId))
                }
            })
        } catch (exception) {}
    })
}

export function* getReservationsByParamSaga() {
    yield takeEvery(GET_RESERVATIONS_BY_PARAM, function* ({ referenceNumber, email }) {
        try {
            yield indicateProgress(['getReservationsByParam'], function* () {
                const apiLanguageCode = yield select(apiLanguageCodeSelector)
                const response = yield call(api.getReservationsByParam, referenceNumber, email, apiLanguageCode)
                if (response.reservations.length > 0) {
                    const entities = {}
                    const result = []

                    // transform reservations data to a format is used for reservations storage
                    const modifiedResponse = response.reservations.map(reservation => {
                        const { reservationId, ...reservationData } = reservation

                        const modifiedReservationData = {
                            ...reservationData,
                            reservationId,
                        }
                        entities[reservationId] = modifiedReservationData
                        result.push(reservationId)
                    })

                    const transformedData = {
                        entities: {
                            reservations: entities,
                        },
                        result,
                    }

                    yield put(setReservationsByParam(response.reservations))
                    //set guest user reservations also to usual reservations state as this data also should be used in approptiate components as reservation
                    yield put(actions.receivedReservations(transformedData, null))
                    yield put(setReservations([response.reservations[0].reservationId]))
                } else yield put(getReservationsByParamError())
            })
        } catch (error) {}
    })
}

export function* setAutoRenewSaga() {
    yield takeEvery(constants.SET_AUTO_RENEW, function* ({ reservation, autoRenew }) {
        const apiLanguageCode = yield select(apiLanguageCodeSelector)

        yield call(indicateProgress, ['setAutoRenew'], api.setAutoRenew, reservation, autoRenew, apiLanguageCode)
        yield put(actions.updatedAutoRenew(normalize({ ...reservation, autoRenew }, reservationSchema)))
    })
}

export function* cancelReservationAmendPolicySaga() {
    yield takeEvery(constants.GET_RESERVATION_CANCEL_AMEND_POLICY, function* ({ reservationId }) {
        const user = yield select(userSelector)

        const guestUserReservartionData = yield select(guestUserReservationsSelector)
        //check if guest user reservation  data exists and use it in the next api call, as it means user is in guest user manage booking flow

        const apiLanguageCode = yield select(apiLanguageCodeSelector)
        const policy = yield call(
            indicateProgress,
            ['reservationCancelAmendPolicy'],
            api.reservationCancelAmendPolicy,
            reservationId,
            guestUserReservartionData?.emailAddress || user?.email,
            apiLanguageCode
        )
        //if user is in guest user manage booking flow, then set amend policy data as it's needed for the BookingDetails component
        if (guestUserReservartionData?.emailAddress) {
            yield put(setGuestReservationCancelAmendPolicy(policy))
        }
        yield put(actions.reservationCancelAmendPolicyResponse(policy))
    })
}

export function* cancelReservationSaga() {
    yield takeEvery(constants.CANCEL_RESERVATION, function* ({ reservationId }) {
        const user = yield select(userSelector)
        //check if guest user reservation  data exists and use it in the next api call, as it means user is in guest user manage booking flow and guest user email should be sent in the cancelReservation api call
        const guestUserReservartionData = yield select(guestUserReservationsSelector)
        const apiLanguageCode = yield select(apiLanguageCodeSelector)

        try {
            const response = yield call(
                indicateProgress,
                ['cancelReservation'],
                api.cancelReservationByAccount,
                reservationId,
                user?.email || guestUserReservartionData?.emailAddress,
                apiLanguageCode
            )

            yield put(actions.cancelReservationResponse(response))
        } catch (error) {
            yield put(actions.cancelReservationResponse(error))
        }
    })
}

export function* clearReservationsSaga() {
    yield takeEvery(LOGOUT, function* () {
        yield put(clearEntity(reservationSchema.key))
    })
}

export function showReceipt(reservationHtml, receiptTab) {
    receiptTab.window.document.write(reservationHtml)
}

export function downloadReceipt(confirmationNumber, reservationHtml) {
    const blob = new Blob([reservationHtml], { type: 'text/html;charset=utf-8' })
    saveAs(blob, `${confirmationNumber}.html`)
}

export function* getReservationReceiptSaga() {
    yield takeEvery(constants.GET_RESERVATION_RECEIPT, function* ({ reservationId, receiptTab }) {
        const { confirmationNumber } = yield select(selectors.reservationSelector(reservationId))
        const { email } = yield select(userSelector)
        const apiLanguageCode = yield select(apiLanguageCodeSelector)
        const { reservationHtml } = yield call(api.getReservationReceipt, reservationId, email, apiLanguageCode)

        if (isMobileAgent()) showReceipt(reservationHtml, receiptTab)
        else downloadReceipt(confirmationNumber, reservationHtml)
    })
}

export function* changeVehicleSaga() {
    const apiLanguageCode = yield select(apiLanguageCodeSelector)

    yield takeLatest(constants.CHANGE_VEHICLE, function* ({ reservationId, selectedVehicles }) {
        const user = yield select(userSelector)

        //check if it's guest user booking flow or not, because for guest user flow should be sent '' instead of token
        const guestUserReservartionData = yield select(guestUserReservationsSelector)
        const response = yield call(
            api.updateReservationVehicleData,
            guestUserReservartionData ? '' : user.token,
            reservationId,
            selectedVehicles,
            apiLanguageCode
        )

        yield put(actions.changeVehicleResponse(response))
    })
}

export function* validateWaitingListKeySaga() {
    yield takeEvery(constants.VALIDATE_WAITING_LIST_KEY, function* ({ waitingListKey }) {
        const { token } = yield select(userSelector)
        const apiLanguageCode = yield select(apiLanguageCodeSelector)
        try {
            const response = yield call(
                indicateProgress,
                'validateWaitingListKey',
                api.validateWaitingListKey,
                token,
                waitingListKey,
                apiLanguageCode
            )
            yield put(actions.validatedWaitingListKey(waitingListKey, response))
        } catch (error) {
            yield put(actions.failedToValidateWaitingListKey(waitingListKey))
        }
    })
}

export function* addToWaitingListSaga() {
    yield takeEvery(constants.ADD_TO_WAITING_LIST, function* ({ rate, grsId }) {
        try {
            const user = yield select(userSelector)
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const result = yield call(
                indicateProgress,
                'addToWaitingList',
                api.addToWaitingList,
                user,
                rate,
                grsId,
                apiLanguageCode
            )

            yield put(actions.addedToWaitingList(result))
        } catch (error) {}
    })
}

export function* cancelWaitingListSaga() {
    yield takeEvery(constants.CANCEL_WAITING_LIST, function* ({ waitingListKey }) {
        try {
            const user = yield select(userSelector)
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            yield call(
                indicateProgress,
                ['cancelWaitingList', `cancelWaitingList-${waitingListKey}`],
                api.cancelWaitingList,
                user.token,
                waitingListKey,
                apiLanguageCode
            )

            yield put(actions.canceledWaitingList(waitingListKey))
        } catch (error) {}
    })
}

export default function* rootSaga() {
    yield fork(locationDetailsSaga)
    yield fork(locationRatesSaga)
    yield fork(locationRateDetailsSaga)
    yield fork(getReservationsSaga)
    yield fork(getReservationsByParamSaga)
    yield fork(setAutoRenewSaga)
    yield fork(cancelReservationAmendPolicySaga)
    yield fork(cancelReservationSaga)
    yield fork(clearReservationsSaga)
    yield fork(getReservationReceiptSaga)
    yield fork(changeVehicleSaga)
    yield fork(validateWaitingListKeySaga)
    yield fork(addToWaitingListSaga)
    yield fork(cancelWaitingListSaga)
}
